import axios, { AxiosResponse, AxiosError } from "axios";
import {
  AuthLoginInput,
  RegisterJobSeekerInput,
  RegisterHRInput,
  RegisterResponse,
  AuthLoginResponse,
  AuthJobSeekerGoogle,
  AuthGoogleSSORequest,
  AuthChangePasswordRequest,
  AuthUpdateCandidateProfileRequest,
  AuthUpdateEmployeerProfileRequest,
  RegisterHROTPInput,
  ResetPasswordInput,
  AuthSendWelcomEmailRequest,
  AuthRequestJoinTeam,
  AuthApproveRequestJoinTeam,
  AuthRegusterWithoutPasswordInput,
  AuthCreatePasswordRequest,
} from "@type/auth";
import { getCookie, setCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

export const authAccessToken = async (
  token: string
): Promise<{ targetRoute: string; onInterviewComplete: string }> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/access_token`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: { token },
      timeout: 10000, // 10 second timeout
    });
    setCookie(
      "authToken",
      response.data.data.token.auth_token,
      import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY
    );
    setCookie("logged_in", "true");

    if (response.data.data.job_id) {
      setCookie("job_id", response.data.data.job_id);
    }

    return {
      targetRoute: response.data.data.targetRoute,
      onInterviewComplete:
        response.data.data.onInterviewComplete || "/dashboard",
    };
  } catch (error) {
    const err = error as AxiosError;
    const message = err.response?.data || "Failed to get access token";
    return { targetRoute: "/", onInterviewComplete: message as string };
  }
};

/**
 * Authenticates a user with email and password
 * @param payload - Login credentials
 * @returns Promise with login response data
 */
export const authLogin = async (
  payload: AuthLoginInput
): Promise<AuthLoginResponse> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/login`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Company-Subdomain": getCookie("CURRENT_COMPANY") || "",
      },
      data: payload,
      timeout: 120000, // 10 second timeout
    });

    return {
      data: response.data.data,
      message: response.data.message,
      status: response.data.code,
    };
  } catch (error) {
    const err = error as AxiosError;

    if (error instanceof Error) {
      catchErrorLog(`failed to authentication, err: ${error.message}`);
    }

    return {
      status: error instanceof Error ? err.response?.status || 400 : 500,
      message: axios.isAxiosError(error)
        ? error.response?.data?.message || error?.message
        : "",
      error: err,
      // @ts-ignore
      data: error instanceof Error ? err.response?.data : null,
    };
  }
};

export const registerJobSeeker = async (
  payload: RegisterJobSeekerInput
): Promise<any> => {
  const response = await axios({
    url: `${apiURL}/auth/register/job-seeker`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Company-Subdomain": getCookie("CURRENT_COMPANY") || "",
    },
    data: payload,
    timeout: 360000,
  });

  return response;
};

/**
 * Registers a new HR/employer user
 * @param payload - HR registration data
 * @returns Promise with registration response
 */
export const registerHR = async (
  payload: RegisterHRInput
): Promise<RegisterResponse> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/register/hr`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: payload,
      timeout: 10000, // 10 second timeout
    });

    return {
      code: response.data.code,
      data: response.data,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to register hr, err: ${error.message}`);
    }

    return {
      code: 400,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Request timed out"
          : "HR registration failed"),
    };
  }
};

/**
 * Authenticates a user with Google OAuth token
 * @param googleToken - Google OAuth access token
 * @returns Promise with login response data
 */
export const authJobSeekerLoginWithGoogle = async (
  body: AuthJobSeekerGoogle
): Promise<AuthLoginResponse> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/job-seeker/google`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Company-Subdomain": getCookie("CURRENT_COMPANY") || "",
      },
      data: body,
      timeout: 120000, // 2 min timeout
    });

    return {
      data: response.data.data,
      message: response.data.message,
      status: response.data.code,
    };
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(
        `failed to authentication candidate via google SSO, err: ${error.message}`
      );
    }

    if (axios.isAxiosError(error)) {
      return {
        status: 500,
        message: error?.response?.data.message,
      };
    }
    return {
      status: 500,
      message: "Request time out",
    };
  }
};

/**
 * Authenticates a user with Google SSO
 * @param body - Google SSO data
 * @returns Promise with login response data
 */
export const authGoogleSSO = async (
  body: AuthGoogleSSORequest
): Promise<AuthLoginResponse> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/google-sso`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Company-Subdomain": getCookie("CURRENT_COMPANY") || "",
      },
      data: body,
      timeout: 600000,
    });

    return {
      data: response.data.data,
      message: response.data.message,
      status: response.data.code,
    };
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(
        `failed to authentication via google SSO, err: ${error.message}`
      );
    }

    if (axios.isAxiosError(error)) {
      return {
        status: error?.response?.status || 500,
        message: error?.response?.data.message,
        data: error?.response?.data || null,
      };
    }
    return {
      status: 500,
      message: "Request time out",
    };
  }
};

export const changePassword = async (
  body: AuthChangePasswordRequest
): Promise<any> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/change-password`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: body,
      timeout: 120000,
    });

    return {
      status: response.data.code,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      status: err.response?.status || 500,
      message: responseData?.message,
      error: err,
    };
  }
};

export const createPassword = async (
  body: AuthCreatePasswordRequest
): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/auth/create-password`,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: body,
  });
};

export const updateCandidateProfile = async (
  body: AuthUpdateCandidateProfileRequest
): Promise<any> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/update-job-seeker-profile`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: body,
      timeout: 120000,
    });

    return {
      status: response.data.code,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      status: err.response?.status || 500,
      message: responseData?.message,
      error: err,
    };
  }
};

export const updateEmployeerProfile = async (
  body: AuthUpdateEmployeerProfileRequest
): Promise<any> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/update-hr-profile`,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: body,
      timeout: 120000,
    });

    return {
      status: response.data.code,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      status: err.response?.status || 500,
      message: responseData?.message,
      error: err,
    };
  }
};

export const checkHRDomain = async (email: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/check-hr-domain`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Company-Subdomain": getCookie("CURRENT_COMPANY") || "",
    },
    data: {
      email,
    },
    timeout: 120000,
  });
};

export const verifyHREmail = async (
  email: string,
  code: string
): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/verify-hr-email`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      email,
      code,
    },
    timeout: 120000,
  })
};

export const registerHRotp = async (body: RegisterHROTPInput): Promise<any> => {
  return await axios({
      url: `${apiURL}/auth/register/hr-with-otp`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: body,
      timeout: 120000,
  });
};

export const authResendCode = async (email: string): Promise<any> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/resend-code`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email,
      },
      timeout: 120000,
    });

    return response;
  } catch (error) {
    console.log(`failed to resend code, err: ${error}`);

    return undefined;
  }
};

export const authGoogleConnect = async (
  sub: string
): Promise<AuthLoginResponse> => {
  try {
    const token = getCookie("authToken");
    const response: AxiosResponse = await axios({
      url: `${apiURL}/auth/connect/google`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: {
        sub: sub,
      },
      timeout: 600000,
    });

    return {
      data: response.data.data,
      message: response.data.message,
      status: response.data.code,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        status: 500,
        message: error?.response?.data.message,
      };
    }
    return {
      status: 500,
      message: "Request time out",
    };
  }
};

export const authOTPJobSeeker = async (email: string): Promise<any> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/set-otp-jobseeker`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email,
      },
      timeout: 300000, // 15 second timeout
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(
        `failed to set OTP email job seeker, err: ${error.message}`
      );
    }
    return undefined;
  }
};

export const authVerifyEmailJobSeeker = async (
  email: string,
  code: string
): Promise<any> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/verify-jobseeker-email`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email,
        code,
      },
      timeout: 300000, // 15 second timeout
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(
        `failed to verify OTP email jobseeker, err: ${error.message}`
      );
    }
    return undefined;
  }
};

export const authResendCodeJobSeeker = async (email: string): Promise<any> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/resend-code-jobseeker`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email,
      },
      timeout: 300000, // 15 second timeout
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(`failed to resend code job seeker, err: ${error.message}`);
    }
    return undefined;
  }
};

export const authForgotPassword = async (email: string): Promise<any> => {
  try {
    const response = await axios({
      url: `${apiURL}/auth/forgot-password`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email,
      },
      timeout: 300000, // 15 second timeout
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(`failed to forgot password, err: ${error.message}`);
    }
    return undefined;
  }
};

export const authResetPassword = async (
  payload: ResetPasswordInput
): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/reset-password`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload,
    timeout: 300000, // 15 second timeout
  });
};

export const authSendWelcomEmail = async (request: AuthSendWelcomEmailRequest): Promise<any> => {
  try {
    const token = getCookie("authToken");
    const response = await axios({
      url: `${apiURL}/auth/send-welcome-email`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      data: request,
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      catchErrorLog(`failed to auth send welcom email, err: ${error.message}`);
    }
    return undefined;
  }
};


export const getInviteTeamDetail = async (email: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/team-invite-detail`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    params: {
      email
    }
  });
};

export const approveJobCollaboration = async (email: string, job_id: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/approve-job-collaboration`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      email,
      job_id
    }
  });
};



export const requestJoinTeam = async (payload: AuthRequestJoinTeam): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/request/join-team`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload
  });
};

export const approveJoinTeam = async (payload: AuthApproveRequestJoinTeam): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/approve/join-team`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload
  });
};

export const authRegisterWithoutPassword = async (payload: AuthRegusterWithoutPasswordInput): Promise<any> => {
  return await axios({
    url: `${apiURL}/auth/register-without-password`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: payload
  });
};


export const authSwitchWorkspace = async (company_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/auth/switch-workspace`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { company_id }
  });
};